import * as THREE from 'three';
import Experience from '../../Experience';
import { InteractableMixin } from '../../Utils/InteractableMixin';

export default class TVModel
{
  constructor()
  {
    this.experience = new Experience();
    this.scene = this.experience.scene;
    this.resources = this.experience.resources;
    this.time = this.experience.time;
    this.debug = this.experience.debug;

    // TVコンポーネントへの参照を保存（後で設定）
    this.tvComponent = null;

    // Debug
    if(this.debug.active)
    {
      this.debugFolder = this.debug.ui.addFolder('TVModel');
      this.debugFolder.close();
    }

    // Setup
    this.resource = this.resources.items.tvModel;
    this.setModel();

    // インタラクション機能を設定
    this.setupInteraction();
  }

  setModel()
  {
    this.model = this.resource.scene;
    this.model.scale.set(0.5, 0.5, 0.5);
    // TVモデルの位置を設定（必要に応じて調整）
    this.model.position.set(-2.6, -1.2, 15);
    this.model.rotation.set(-0.3415, -0.2415,  -0.4415); // Y軸を中心に90度回転

    this.scene.add(this.model);

    // TVスクリーンメッシュを取得（インタラクション対象）
    this.mesh = this.getTVScreen();

    // デバッグ用：モデルの詳細情報を表示
    if(this.debug.active)
    {
      this.addDebugControls();
    }
  }

  setupInteraction()
  {
    // TVModelが完全に読み込まれるまで少し待つ
    setTimeout(() => {
      if (this.mesh) {
        // InteractableMixinの機能を追加
        Object.assign(this, InteractableMixin);
        
        // カスタムのonInteractメソッドを定義
        this.onInteract = (intersectionData) => {
          console.log('🎯 TV Screen was clicked!', intersectionData);
          
          // TVコンポーネントのメソッドを呼び出し
          if (this.tvComponent && typeof this.tvComponent.handleInteract === 'function') {
            this.tvComponent.handleInteract(intersectionData);
          } else {
            // TVコンポーネントが設定されていない場合のフォールバック
            console.log('TV: No TV component found, basic interaction');
          }
        };

        // インタラクション機能を有効化
        this.makeInteractable();
      } else {
        console.warn('❌ TVModel: No mesh available for interaction');
      }
    }, 100);
  }

  handleBasicInteraction()
  {
    // 基本的なインタラクション処理（TVコンポーネントがない場合）
    console.log('Basic TV interaction - toggling screen color');
  }

  // TVコンポーネントへの参照を設定
  setTVComponent(tvComponent)
  {
    this.tvComponent = tvComponent;
  }

  // Debug
  addDebugControls()
  {
    // デバッグ用のコントロールを追加
    const debugObject = {
      positionX: this.model.position.x,
      positionY: this.model.position.y,
      positionZ: this.model.position.z,
      rotationX: this.model.rotation.x,
      rotationY: this.model.rotation.y,
      rotationZ: this.model.rotation.z,
      scaleX: this.model.scale.x,
      scaleY: this.model.scale.y,
      scaleZ: this.model.scale.z
    };

    // Position controls
    this.debugFolder.add(debugObject, 'positionX', -20, 20, 0.1)
      .onChange((value) => { this.model.position.x = value; });
    this.debugFolder.add(debugObject, 'positionY', -20, 20, 0.1)
      .onChange((value) => { this.model.position.y = value; });
    this.debugFolder.add(debugObject, 'positionZ', -20, 20, 0.1)
      .onChange((value) => { this.model.position.z = value; });

    // Rotation controls
    this.debugFolder.add(debugObject, 'rotationX', -Math.PI, Math.PI, 0.1)
      .onChange((value) => { this.model.rotation.x = value; });
    this.debugFolder.add(debugObject, 'rotationY', -Math.PI, Math.PI, 0.1)
      .onChange((value) => { this.model.rotation.y = value; });
    this.debugFolder.add(debugObject, 'rotationZ', -Math.PI, Math.PI, 0.1)
      .onChange((value) => { this.model.rotation.z = value; });

    // Scale controls
    this.debugFolder.add(debugObject, 'scaleX', 0.1, 3, 0.1)
      .onChange((value) => { this.model.scale.x = value; });
    this.debugFolder.add(debugObject, 'scaleY', 0.1, 3, 0.1)
      .onChange((value) => { this.model.scale.y = value; });
    this.debugFolder.add(debugObject, 'scaleZ', 0.1, 3, 0.1)
      .onChange((value) => { this.model.scale.z = value; });
  }

  // TVの位置を設定するメソッド
  setPosition(x, y, z)
  {
    if(this.model)
    {
      this.model.position.set(x, y, z);
    }
  }

  // TVの回転を設定するメソッド
  setRotation(x, y, z)
  {
    if(this.model)
    {
      this.model.rotation.set(x, y, z);
    }
  }

  // TVのスケールを設定するメソッド
  setScale(x, y, z)
  {
    if(this.model)
    {
      if(typeof x === 'number' && y === undefined && z === undefined)
      {
        // 単一の値が渡された場合、すべての軸に適用
        this.model.scale.setScalar(x);
      }
      else
      {
        this.model.scale.set(x, y, z);
      }
    }
  }

  // TVモデルのメッシュを取得（TVクラスとの連携用）
  getTVScreen()
  {
    // カスタムで設定されたスクリーンメッシュがある場合はそれを返す
    if (this.customScreenMesh) {
      // console.log('Using custom screen mesh:', this.customScreenMesh.name);
      return this.customScreenMesh;
    }

    let screenMesh = null;

    this.model.traverse((child) => {
      if(child.isMesh) {
        // console.log(`Checking mesh: "${child.name}" (Type: ${child.type})`);
        //TODO: TVScreenの名称で Blender TVのScreenを実装している。必要に応じて名称を変更
        if (child.name === 'TVScreen') {
          screenMesh = child;
          return;
        }
        
        // より柔軟な検索条件（フォールバック）
        const name = child.name.toLowerCase();
        if (name.includes('tvscreen') || 
            name.includes('tv_screen') ||
            name.includes('screen') || 
            name.includes('display') || 
            name.includes('monitor')) {
          // console.log(`✅ Found potential screen mesh: "${child.name}"`);
          if (!screenMesh) {
            screenMesh = child;
          }
        }
      }
    });

    return screenMesh;
  }

  // リソースのクリーンアップ
  destroy()
  {
    if(this.model)
    {
      this.scene.remove(this.model);
    }

    if(this.debugFolder && this.debug.active)
    {
      this.debugFolder.destroy();
    }
  }
}